import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  output,
  viewChild,
  InputSignal,
  OutputEmitterRef,
  Signal,
  ChangeDetectionStrategy,
  signal,
  WritableSignal,
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import {
  ModalController,
  Platform,
  IonButton,
  IonIcon,
  IonCheckbox,
  IonRippleEffect,
} from '@ionic/angular/standalone';
import { CorrectFeedback } from '../../../models/feedback/correct-feedback.model';
import { Feedback } from '../../../models/feedback/feedback.model';
import { InCorrectFeedback } from '../../../models/feedback/incorrect-feedback.model';
import { InformationalFeedback } from '../../../models/feedback/informational-feedback.model';
import { SelfCheckFeedback } from '../../../models/feedback/self-check-feedback.model';
import { AnswersSelfExam } from '../../../models/on-interaction/answers-self-exam.model';
import { SeenFeedback } from '../../../models/on-interaction/seen-feedback.model';
import { QuestionStep } from '../../../models/step/question-step.model';
import { DynamicComponentLoader } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { FeedbackType } from '../../../shared/enums/feedback-type.enum';
import { ThankYouType } from '../../../shared/enums/thank-you-type.enum';
import { fromResize } from '@lyceo/shared/util-resize-observable';
import { FeedbackPlaceholderDirective } from './feedback-placeholder.directive';
import { SelfCheckHelpComponent } from './self-check-help/self-check-help.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { addIcons } from 'ionicons';
import {
  informationCircleOutline,
  chevronUpOutline,
  arrowForward,
} from 'ionicons/icons';
import { NgTemplateOutlet } from '@angular/common';
import {
  DisplayTablePipe,
  DisplayMathPipe,
  QuestionTableService,
  QuestionTableDataContainer,
  QuestionTableComponent,
} from '@lyceo/shared/question-display/feature-main';

export interface OnNextQuestionData {
  userConfirmedGivenAnswerWasCorrect: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-feedback',
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss'],
  imports: [
    NgTemplateOutlet,
    IonButton,
    IonIcon,
    IonCheckbox,
    ReactiveFormsModule,
    FeedbackPlaceholderDirective,
    IonRippleEffect,
    DisplayMathPipe,
    DisplayTablePipe,
  ],
  host: {
    '(window:keydown.shift.enter)': 'nextQuestion()',
    '(window:keydown.alt.u)': 'toggleFeedbackView()',
  },
})
export class FeedbackComponent implements AfterViewInit {
  public platform: Platform = inject(Platform);
  public dynamicComponentLoader: DynamicComponentLoader = inject(
    DynamicComponentLoader,
  );
  private readonly modalController: ModalController = inject(ModalController);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly questionTableService: QuestionTableService =
    inject(QuestionTableService);

  /**
   * Enum template injection
   */
  public readonly ThankYouType: typeof ThankYouType = ThankYouType;

  /**
   * The list of feedback items. In case a question
   * has multiple answer items, multiple feedback items
   * will be returned.
   */
  public readonly feedback: InputSignal<Feedback | undefined | null> = input<
    Feedback | undefined | null
  >();

  /**
   * The current step which is holding the feedback.
   */
  public readonly step: InputSignal<QuestionStep | undefined> =
    input<QuestionStep>();

  /**
   * Flag to disable the button in case the form control is invalid.
   */
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);

  /**
   * Emit the event to the QuestionSupplier to go to the next question.
   */

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  public readonly onNextQuestion: OutputEmitterRef<
    SeenFeedback | AnswersSelfExam
  > = output<SeenFeedback | AnswersSelfExam>();

  /**
   * The container holding the feedback.
   */
  public readonly feedbackContainer: Signal<ElementRef> =
    viewChild.required<ElementRef>('feedbackContainer');

  /**
   * FormControl that is only shown for self exam questions. This will be used
   * by the user to confirm whether or not he/she thinks that the given answer
   * matches the correct answer (which is provided as feedback).
   */
  public givenAnswerWasCorrectFormControl: UntypedFormControl =
    new UntypedFormControl(false);

  /**
   * Expose the FeedbackType enum to the template.
   */
  public feedbackType: typeof FeedbackType = FeedbackType;

  /**
   * Whether or not feedback is shown.
   */
  public readonly showFeedback: WritableSignal<boolean> = signal(true);

  /**
   * Whether the compact or the extended feedback is shown.
   */
  public extendedFeedbackToggleEnabled: boolean = false;

  /**
   * Whether the feedback is correct.
   */
  public get isCorrectFeedback(): boolean {
    return this.feedback() instanceof CorrectFeedback;
  }

  /**
   * If the feedback is correct.
   */
  public get isIncorrectFeedback(): boolean {
    return this.feedback() instanceof InCorrectFeedback;
  }

  /**
   * If the feedback is self check.
   */
  public get isSelfCheckFeedback(): boolean {
    return this.feedback() instanceof SelfCheckFeedback;
  }

  /**
   * If the feedback is informational.
   */
  public get isInformationalFeedback(): boolean {
    return this.feedback() instanceof InformationalFeedback;
  }

  /**
   * The question display placeholder will be used to display the correct question.
   */
  public readonly feedbackPlaceholder: Signal<
    FeedbackPlaceholderDirective | undefined
  > = viewChild(FeedbackPlaceholderDirective);

  /**
   * Data for tables used in the question.
   */
  private compactFeedbackTableDataContainer:
    | QuestionTableDataContainer
    | undefined;

  private extendedFeedbackTableDataContainer:
    | QuestionTableDataContainer
    | undefined;

  constructor() {
    addIcons({ informationCircleOutline, chevronUpOutline, arrowForward });

    effect(() => {
      const value: Feedback | undefined | null = this.feedback();

      this.compactFeedbackTableDataContainer =
        value && value.compact
          ? this.questionTableService.tablesJsonToQuestionTableData(
              value.compact,
            )
          : undefined;

      this.extendedFeedbackTableDataContainer =
        value && value.extended
          ? this.questionTableService.tablesJsonToQuestionTableData(
              value.extended,
            )
          : undefined;
    });
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    fromResize(this.feedbackContainer().nativeElement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resize: ResizeObserverEntry) => {
        if (resize.contentRect.width > 700) {
          this.showFeedback.set(true);
        }
      });

    const feedbackPlaceholder: FeedbackPlaceholderDirective | undefined =
      this.feedbackPlaceholder();
    if (feedbackPlaceholder) {
      feedbackPlaceholder.ready().then(() => {
        this.injectTables();
      });
    }
  }

  /**
   * Replaces the table placeholders with newly Injected QuestionTableComponents.
   */
  private injectTables(): void {
    const feedbackPlaceholder: FeedbackPlaceholderDirective | undefined =
      this.feedbackPlaceholder();
    if (!feedbackPlaceholder) {
      return;
    }

    const instances: QuestionTableComponent[] =
      this.dynamicComponentLoader.injectReplacementComponentByElement<QuestionTableComponent>(
        feedbackPlaceholder.viewContainerRef,
        QuestionTableComponent,
        '.question-table-placeholder',
      );
    instances.forEach((instance, key) => {
      if (
        this.extendedFeedbackToggleEnabled &&
        this.extendedFeedbackTableDataContainer
      ) {
        instance.setData(
          this.extendedFeedbackTableDataContainer.questionTableData[key],
        );
      } else if (this.compactFeedbackTableDataContainer) {
        instance.setData(
          this.compactFeedbackTableDataContainer.questionTableData[key],
        );
      }
    });
  }

  /**
   * Open a modal to show some help text about the self check that is available
   * for self exam questions.
   */
  public async openSelfCheckHelpModal(): Promise<void> {
    const modal: HTMLIonModalElement = await this.modalController.create({
      component: SelfCheckHelpComponent,
      cssClass: 'lib-alfred-client-modal-small',
    });

    await modal.present();
  }

  /**
   * Emit the onNextQuestion event.
   */
  public nextQuestion(): void {
    const isSelfCheckFeedback: boolean =
      this.feedback() instanceof SelfCheckFeedback;

    if (isSelfCheckFeedback) {
      this.onNextQuestion.emit(
        new AnswersSelfExam(
          this.givenAnswerWasCorrectFormControl.value,
          this.step()?.answer,
        ),
      );
      return;
    }

    this.onNextQuestion.emit(new SeenFeedback());
  }

  /**
   * Toggle if the feedback is shown or hidden.
   */
  public toggleFeedbackView(): void {
    this.showFeedback.set(!this.showFeedback());
  }

  /**
   * Toggle if the compact or extended feedback is shown.
   */
  public toggleExtendedFeedback(): void {
    this.extendedFeedbackToggleEnabled = !this.extendedFeedbackToggleEnabled;

    const feedbackPlaceholder: FeedbackPlaceholderDirective | undefined =
      this.feedbackPlaceholder();
    if (feedbackPlaceholder) {
      feedbackPlaceholder.ready().then(() => {
        this.injectTables();
      });
    }
  }
}
